import * as React from "react";
import { FormPrompt, FormPromptWithSaveProps } from "shared/prompt";
import { HTMLChakraProps, chakra, Box } from "@chakra-ui/react";
import {
  useAuditChartReadOnly,
  useChartTrackingContext,
  useSpeechToTextContext,
} from "contexts";
import { useListChartElements } from "modules/charts-shared/hooks";
import { useLocation } from "react-router-dom";

type ChartFormProps = {
  onRouteChangeSave: FormPromptWithSaveProps["onSave"];
  onSilentSave?: FormPromptWithSaveProps["onSilentSave"];
  isPromptSaveLoading: FormPromptWithSaveProps["isSaveLoading"];
  isDirty: boolean;
  disabled?: boolean;
  sectionId?: string;
  setChartElementsList?(chartElementList: Record<string, string>[]): void;
  autoSaveOnNavigate?: boolean;
} & React.ComponentProps<"form"> &
  HTMLChakraProps<"form">;

function ChartForm(props: ChartFormProps) {
  const {
    onRouteChangeSave,
    onSilentSave,
    isDirty,
    isPromptSaveLoading,
    sectionId,
    children,
    disabled,
    autoSaveOnNavigate = false,
    ...formProps
  } = props;

  const formRef = React.useRef<HTMLFormElement>(null);
  const { isCurrentChartCompleted, updateChartElements } =
    useChartTrackingContext();
  const { isAuditChartReadOnly } = useAuditChartReadOnly();
  const isInteractionLocked = Boolean(
    disabled || isAuditChartReadOnly || isCurrentChartCompleted
  );
  const location = useLocation();
  const fromAudits = location.pathname.includes("audits");
  const fromMedicalRecords = location.pathname.includes("medical-records");

  const secondaryText = React.useMemo(
    () =>
      fromAudits
        ? "Audit changes are pending in this section. Save them before navigating to another section."
        : ["SHPI", "SROS", "SPEX", "SCHC", "SASS"].includes(sectionId || "")
        ? `You must fill out every subsection
        for this section before the chart can be completed.`
        : "",
    [fromAudits, sectionId]
  );

  const { setCodesList } = useSpeechToTextContext();
  useListChartElements(children, updateChartElements, setCodesList);

  return (
    <>
      <FormPrompt
        mainText="You have unsaved changes in this section!"
        onSave={onRouteChangeSave}
        title="Warning"
        when={isDirty && !isInteractionLocked}
        isSaveLoading={isPromptSaveLoading}
        secondaryText={secondaryText}
        skipDialog={autoSaveOnNavigate}
        onSilentSave={autoSaveOnNavigate ? onSilentSave : undefined}
      />
      <Box position="relative" height="100%">
        <chakra.fieldset
          minW={fromMedicalRecords ? "100vw" : "unset"}
          disabled={isInteractionLocked}
          pointerEvents={isInteractionLocked ? "none" : undefined}
        >
          <chakra.form
            ref={formRef}
            padding="1.5rem"
            sx={{ "> :not(:last-child)": { marginBottom: "1rem" } }}
            height="100%"
            disabled={disabled}
            {...formProps}
          >
            {children}
          </chakra.form>
        </chakra.fieldset>
      </Box>
    </>
  );
}

export { ChartForm };
